import {
  Box, Grid, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import api from '../../../../services/api';
import { PageSetting } from '../../../../contexts/PagesContext';

interface Props {
  pageId: string | null;

  url?: string
  title?: string
  settings?: PageSetting
  handleGoogleSearchConsoleUrlChange?: (url: string, script: string) => void
}

function GoogleSearchConsoleSetting(
  props: Props,
) {
  const {
    pageId,
    url,
    title,
    settings,
    handleGoogleSearchConsoleUrlChange,
  } = props;

  const [isRegistered, setIsRegistered] = useState<boolean>(false);
  const [loginType, setLoginType] = useState<string>('search-console');
  const [failMessage, setFailMessage] = useState<string>(null);
  const [successMessage, setSuccessMessage] = useState<string>(null);

  const siteVerificationScope = 'https://www.googleapis.com/auth/siteverification';
  const searchConsoleScope = 'https://www.googleapis.com/auth/webmasters';

  const addPage = async (token: string | null) => {
    setFailMessage(null);
    setSuccessMessage(null);
    try {
      const accessToken = encodeURIComponent(token);
      const response = await api.get(`/v1/pages/search-console/${pageId}/add?url=${url}&token=${accessToken}`);

      if (response.status !== 200 || response?.data?.original?.error) {
        const error = response?.data?.original?.error;
        if (error === 'add') {
          setFailMessage('Linking failed. (Could not add property.)');
        } else {
          setFailMessage('Could not link.');
        }
        console.error('addPage error:', response.status, response.statusText);
      } else {
        setSuccessMessage('The property has been added to your account. Please log in again to verify the property.');
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        handleLogin('site-verification');
        setLoginType('site-verification');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const verifyPage = async (token: string | null) => {
    setFailMessage(null);
    try {
      const accessToken = encodeURIComponent(token);
      const response = await api.get(`/v1/pages/search-console/${pageId}/verify?url=${url}&token=${accessToken}`);

      if (response.status !== 200 || response?.data?.original?.error) {
        setFailMessage('Could not link.');
        const error = response?.data?.original?.error;
        if (error === 'tag') {
          setFailMessage('Linking failed. (Could not obtain information required for property verification.)');
        } else if (error === 'verify') {
          setFailMessage('Linking failed. (Please verify that the page is published and try again after some time.)');
        }
        console.error('verifyPage error:', response.status, response.statusText);
        setSuccessMessage(null);
      } else {
        handleGoogleSearchConsoleUrlChange(url, response.data.original.script);
        setIsRegistered(true);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const onSuccess = (response: any) => {
    const accessToken = response.access_token;
    if (accessToken && accessToken !== '') {
      if (loginType === 'search-console') {
        addPage(accessToken);
      } else {
        verifyPage(accessToken);
      }
    } else {
      console.error('Google Authentication failed.');
    }
  };

  const searchConsoleLogin = useGoogleLogin({
    onSuccess,
    scope: searchConsoleScope,
  });

  const siteVerificationLogin = useGoogleLogin({
    onSuccess,
    scope: siteVerificationScope,
  });

  const handleLogin = (type: string) => {
    if (type === 'search-console') {
      searchConsoleLogin();
    } else {
      siteVerificationLogin();
    }
  };

  useEffect(() => {
    if (settings && settings.google_search_console_url && url) {
      if (settings.google_search_console_url === url) {
        setIsRegistered(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  return (
    <Box display="flex" flexDirection="column" rowGap="10px">
      <Box>
        <h2 className="mb-2">Google Search Console</h2>
      </Box>
      <Box className="pb-2">
        <hr className="mt-4" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" className="text-bold">
              Registered Page
            </Typography>
            <Grid
              item
              xs={12}
              style={{
                padding: '10px',
                backgroundColor: 'rgb(245, 247, 251)',
                borderRadius: '3px',
              }}
            >
              <Typography variant="caption">
                URL: https://
                {url}
              </Typography>
              <br />
              <Typography variant="caption">
                Title:
                {' '}
                {title}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <hr className="mt-4" />
        <Grid container spacing={2}>
          <Grid item xs={12} className="mb-3">
            <Typography variant="caption">
              Connect this page with Google Search Console to monitor search performance.
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            { !isRegistered && (
              <label className="btn btn-primary mb-2" onClick={() => handleLogin(loginType)}>
                Connect
              </label>
            )}
            { isRegistered && (
              <label className="btn btn-primary mb-2 disabled">
                Linked
              </label>
            )}
          </Grid>
          { !isRegistered && successMessage && (
            <Grid item xs={12}>
              <label className="mb-2 text-success">
                {successMessage}
              </label>
            </Grid>
          )}
          { !isRegistered && failMessage && (
            <Grid item xs={12}>
              <label className="mb-2 text-danger">
                {failMessage}
              </label>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
}

export default GoogleSearchConsoleSetting;
